import type {
  TemplatesAddTemplateSectionInput,
  TemplatesAddTemplateSectionOutput,
  TemplatesCreateTemplateInput,
  TemplatesCreateTemplateOutput,
  TemplatesDeleteTemplateSectionInput,
  TemplatesDeleteTemplateSectionOutput,
  TemplatesDiscardTemplateDraftInput,
  TemplatesDiscardTemplateDraftOutput,
  TemplatesExcludeTemplateBlocksInput,
  TemplatesExcludeTemplateBlocksOutput,
  TemplatesInstantiateTemplateInput,
  TemplatesReadInstanceValuesInput,
  TemplatesReadInstanceValuesOutput,
  TemplatesInstantiateTemplateOutput,
  TemplatesListTemplatesOutput,
  TemplatesMergeTemplateSectionsInput,
  TemplatesMergeTemplateSectionsOutput,
  TemplatesPublishTemplateInput,
  TemplatesPublishTemplateOutput,
  TemplatesReadTemplateInput,
  TemplatesReadTemplateOutput,
  TemplatesReinstateTemplateInput,
  TemplatesReinstateTemplateOutput,
  TemplatesRestoreTemplateBlocksInput,
  TemplatesRestoreTemplateBlocksOutput,
  TemplatesRetireTemplateInput,
  TemplatesRetireTemplateOutput,
  TemplatesSplitTemplateSectionInput,
  TemplatesSplitTemplateSectionOutput,
  TemplatesUpdateTemplateInput,
  TemplatesUpdateTemplateOutput,
  TemplatesUpdateTemplateSectionInput,
  TemplatesUpdateTemplateSectionOutput,
  TemplatesWriteUpInput,
  TemplatesWriteUpOutput,
  TemplatesSetFieldValueInput,
  TemplatesSetFieldValueOutput,
  TemplatesAddTableRowInput,
  TemplatesAddTableRowOutput,
  TemplatesAddEntryInput,
  TemplatesEntryWriteOutput,
  TemplatesUpdateEntryInput,
  TemplatesRemoveEntryInput,
  TemplatesRemoveEntryOutput,
} from '@vivport/backend/types';
import { executeTool, type ExecuteToolResponse } from './tools-api';
import { CAPTURE_WRITE_TIMEOUT_MS } from '@/utils/mutation-retry';

// Capture writes only. The write-up runs a long LLM turn through this same bridge, so it and
// every read keep axios's default of no timeout.
const CAPTURE_TRANSPORT = { timeoutMs: CAPTURE_WRITE_TIMEOUT_MS } as const;

// Every template mutation and read goes through the SAME governed tools Neo uses, via the
// manual bridge. The backend's ValidationError messages are written for members — callers
// surface them verbatim.
export function assertToolSucceeded<TOutput>(
  result: ExecuteToolResponse<TOutput>
): ExecuteToolResponse<TOutput> {
  if (result.status === 'FAILED' || result.status === 'EXPIRED') {
    throw new Error(result.error ?? 'Tool execution failed');
  }
  return result;
}

export function requireToolOutput<TOutput>(result: ExecuteToolResponse<TOutput>): TOutput {
  assertToolSucceeded(result);
  if (result.output === undefined) {
    throw new Error(result.error ?? 'Tool returned no output');
  }
  return result.output;
}

export const createTemplate = (input: TemplatesCreateTemplateInput) =>
  executeTool<TemplatesCreateTemplateInput, TemplatesCreateTemplateOutput>(
    'templates.createTemplate',
    input
  );

export const readTemplate = (input: TemplatesReadTemplateInput) =>
  executeTool<TemplatesReadTemplateInput, TemplatesReadTemplateOutput>(
    'templates.readTemplate',
    input
  );

export const listTemplates = () =>
  executeTool<Record<string, never>, TemplatesListTemplatesOutput>('templates.listTemplates', {});

export const updateTemplate = (input: TemplatesUpdateTemplateInput) =>
  executeTool<TemplatesUpdateTemplateInput, TemplatesUpdateTemplateOutput>(
    'templates.updateTemplate',
    input
  );

export const addTemplateSection = (input: TemplatesAddTemplateSectionInput) =>
  executeTool<TemplatesAddTemplateSectionInput, TemplatesAddTemplateSectionOutput>(
    'templates.addTemplateSection',
    input
  );

export const updateTemplateSection = (input: TemplatesUpdateTemplateSectionInput) =>
  executeTool<TemplatesUpdateTemplateSectionInput, TemplatesUpdateTemplateSectionOutput>(
    'templates.updateTemplateSection',
    input
  );

export const mergeTemplateSections = (input: TemplatesMergeTemplateSectionsInput) =>
  executeTool<TemplatesMergeTemplateSectionsInput, TemplatesMergeTemplateSectionsOutput>(
    'templates.mergeTemplateSections',
    input
  );

export const splitTemplateSection = (input: TemplatesSplitTemplateSectionInput) =>
  executeTool<TemplatesSplitTemplateSectionInput, TemplatesSplitTemplateSectionOutput>(
    'templates.splitTemplateSection',
    input
  );

export const excludeTemplateBlocks = (input: TemplatesExcludeTemplateBlocksInput) =>
  executeTool<TemplatesExcludeTemplateBlocksInput, TemplatesExcludeTemplateBlocksOutput>(
    'templates.excludeTemplateBlocks',
    input
  );

export const restoreTemplateBlocks = (input: TemplatesRestoreTemplateBlocksInput) =>
  executeTool<TemplatesRestoreTemplateBlocksInput, TemplatesRestoreTemplateBlocksOutput>(
    'templates.restoreTemplateBlocks',
    input
  );

export const deleteTemplateSection = (input: TemplatesDeleteTemplateSectionInput) =>
  executeTool<TemplatesDeleteTemplateSectionInput, TemplatesDeleteTemplateSectionOutput>(
    'templates.deleteTemplateSection',
    input
  );

export const publishTemplate = (input: TemplatesPublishTemplateInput) =>
  executeTool<TemplatesPublishTemplateInput, TemplatesPublishTemplateOutput>(
    'templates.publishTemplate',
    input
  );

export const retireTemplate = (input: TemplatesRetireTemplateInput) =>
  executeTool<TemplatesRetireTemplateInput, TemplatesRetireTemplateOutput>(
    'templates.retireTemplate',
    input
  );

export const reinstateTemplate = (input: TemplatesReinstateTemplateInput) =>
  executeTool<TemplatesReinstateTemplateInput, TemplatesReinstateTemplateOutput>(
    'templates.reinstateTemplate',
    input
  );

export const discardTemplateDraft = (input: TemplatesDiscardTemplateDraftInput) =>
  executeTool<TemplatesDiscardTemplateDraftInput, TemplatesDiscardTemplateDraftOutput>(
    'templates.discardTemplateDraft',
    input
  );

export const instantiateTemplate = (input: TemplatesInstantiateTemplateInput) =>
  executeTool<TemplatesInstantiateTemplateInput, TemplatesInstantiateTemplateOutput>(
    'templates.instantiateTemplate',
    input
  );

export const writeUpTemplate = (input: TemplatesWriteUpInput) =>
  executeTool<TemplatesWriteUpInput, TemplatesWriteUpOutput>('templates.writeUp', input);

export const setFieldValue = (input: TemplatesSetFieldValueInput) =>
  executeTool<TemplatesSetFieldValueInput, TemplatesSetFieldValueOutput>('templates.setFieldValue', input, CAPTURE_TRANSPORT);

export const addTableRow = (input: TemplatesAddTableRowInput) =>
  executeTool<TemplatesAddTableRowInput, TemplatesAddTableRowOutput>('templates.addTableRow', input, CAPTURE_TRANSPORT);

export const addEntry = (input: TemplatesAddEntryInput) =>
  executeTool<TemplatesAddEntryInput, TemplatesEntryWriteOutput>('templates.addEntry', input, CAPTURE_TRANSPORT);

export const updateEntry = (input: TemplatesUpdateEntryInput) =>
  executeTool<TemplatesUpdateEntryInput, TemplatesEntryWriteOutput>('templates.updateEntry', input, CAPTURE_TRANSPORT);

export const removeEntry = (input: TemplatesRemoveEntryInput) =>
  executeTool<TemplatesRemoveEntryInput, TemplatesRemoveEntryOutput>('templates.removeEntry', input);

export const readInstanceValues = (input: TemplatesReadInstanceValuesInput) =>
  executeTool<TemplatesReadInstanceValuesInput, TemplatesReadInstanceValuesOutput>(
    'templates.readInstanceValues',
    input
  );
